import { inject, Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { CommerceProduct } from "../models/commerce-product.model";
import {
  addDoc,
  collection, deleteDoc,
  doc, Firestore,
  getDoc,
  getDocs,
  onSnapshot, Query,
  query, setDoc,
  Unsubscribe, updateDoc,
  where
} from "@angular/fire/firestore";
import { ImageModel } from "../models/image.model";
import { UploadService } from "./upload.service";
import { ObjectHelper } from "../helpers/object.helper";
import { OrganizationService } from "./organization.service";
import { IdGeneratorHelper } from "../helpers/id-generator.helper";
import { CommerceCategory } from "../models/commerce-category.model";

@Injectable({
  providedIn: 'root',
})
export class ProductService {

  private uploadService = inject(UploadService);
  private organizationService = inject(OrganizationService);
  private firestore = inject(Firestore);
  private products$: [string, BehaviorSubject<CommerceProduct[]>];
  private unsubscribeProducts: Unsubscribe = () => {return;};

  /**
   * Download the products TSV file
   * @param shopId The shop id
   * @param shopUrl The shop url
   * @param products The products to download
   * @param categories The categories to download
   */
  async downloadProductTSV(shopId: string, shopUrl: string,
    products: CommerceProduct[], categories: CommerceCategory[]): Promise<void> {
    const categoryById = new Map<string, CommerceCategory>(
      (categories ?? []).map(c => [c.id, c])
    );
    const filteredProducts = (products ?? []).filter(p =>
      (p.categories ?? []).some(catId => categoryById.get(catId)?.shop === shopId)
    );

    const escapeTSV = (value: string): string => {
      return '"' + value.replace(/"/g, '""') + '"';
    };

    const headers = [
      'title',
      'id',
      'description',
      'price',
      'condition',
      'availability',
      'link',
      'image_link',
      'additional_image_link',
      'shipping',
    ];
    const rows: string[] = [];
    rows.push(headers.join('\t'));
    for (const p of filteredProducts ?? []) {
      const imgs = (p.images || []).filter(i => !!i?.path);
      const primary = imgs.find(i => i?.default) ?? imgs[0];
      const imageLink = primary?.path || '';
      const additionalImageLink = imgs.filter(i => i !== primary).map(i => i.path).join(',');
      const row = [
        escapeTSV(p.name),
        escapeTSV(p.technicalName || p.id),
        escapeTSV(p.description),
        escapeTSV(p.baitPrice.toFixed(2).toString() + ' EUR'),
        `"new"`,
        `"in_stock"`,
        escapeTSV(shopUrl ? `${shopUrl}/product/${p.name}` : ''),
        escapeTSV(imageLink),
        escapeTSV(additionalImageLink),
        `"fr"`,
      ];
      rows.push(row.join('\t'));
    }
    const tsv = rows.join('\n');
    const blob = new Blob([tsv], { type: 'text/tab-separated-values;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const date = new Date().toISOString().slice(0, 10);
    a.download = `products_${date}.tsv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  /**
   * Retrieves a Firestore query to fetch products based on the provided organization ID.
   * @param organizationId - The unique identifier of the organization whose products are to be fetched.
   * @returns A Firestore query object configured to retrieve products belonging to the specified organization ID.
   */
  getProductsQuery(organizationId: string): Query<CommerceProduct> {
    return query(
      collection(this.firestore, 'product'),
      where('organizationId', '==', organizationId)
    ).withConverter(CommerceProduct.converter);
  }

  /**
   * Get all the products
   * @param organizationId organizationId
   * @returns The observable for products
   */
  public products(organizationId: string): BehaviorSubject<CommerceProduct[]> {
    if (this.products$?.[0] !== organizationId) {
      this.unsubscribeProducts()
      this.products$ = [organizationId, new BehaviorSubject<CommerceProduct[]>([])];
      const q = this.getProductsQuery(organizationId);
      this.unsubscribeProducts = onSnapshot(q, snapshot => {
        if (snapshot.empty) {
          return this.products$[1].next([]);
        }
        this.products$[1].next(snapshot.docs.map(doc => doc.data()));
      });
    }
    return this.products$[1];
  }

  /**
   * Get the product with the given id
   * @param id The product id
   * @returns The product
   */
  async product(id: string): Promise<CommerceProduct> {
    const ref = doc(this.firestore, 'product', id)
      .withConverter(CommerceProduct.converter);
    const docSnap = await getDoc(ref);
    if (!docSnap.exists()) {
      return null;
    }
    return docSnap.data();
  }

  /**
   * Get the product with the given name belonging to the given configuration
   * @param organizationId The organization id
   * @param name The product name
   * @returns First product with same name
   */
  async isAvailableTechnicalName(organizationId: string, name: string): Promise<boolean> {
    const q = query(
      collection(this.firestore, 'product'),
      where('organizationId', '==', organizationId),
      where('technicalName', '==', name))
      .withConverter(CommerceProduct.converter);
    return getDocs(q)
      .then(snapshot => {
        return snapshot.empty;
      })
  }

  /**
   * Get products linked with the configuration id
   * @param id The configuration id
   * @returns Linked products
   */
  async productByConfiguration(id: string): Promise<CommerceProduct[]> {
    const q = query(
      collection(this.firestore, 'product'),
      where('variations', 'array-contains', id))
      .withConverter(CommerceProduct.converter);
    const snapshot = await getDocs(q);
    if (snapshot.empty) {
      return [];
    }
    return snapshot.docs.map(doc => doc.data());
  }

  /**
   * Create a new product for the given configuration with the given name
   * @param product The product to create
   * @returns The created product id
   */
  async addProduct(product: Partial<CommerceProduct>): Promise<string> {
    const ref = collection(this.firestore, 'product')
      .withConverter(CommerceProduct.converter);
    return addDoc(ref, product)
      .then(ref => ref.id);
  }

  /**
   * Update the given product
   * @param id The product id to update
   * @param product The product to update
   * @returns void
   */
  updateProduct(id: string, product: Partial<CommerceProduct>): Promise<void> {
    const ref = doc(this.firestore, 'product', id)
      .withConverter(CommerceProduct.converter);
    return updateDoc(ref, ObjectHelper.anonymize(product));
  }

  /**
   * Copy the given product, appending (Copy) to its name
   * @param product The product to copy
   * @param technicalName The product to copy
   * @returns The copied product name
   */
  async copyProduct(product: CommerceProduct, technicalName: string): Promise<string> {
    const ref = doc(collection(this.firestore, 'product')).withConverter(CommerceProduct.converter);
    if (product.pdf) {
      const newPdf = product.pdf.replace(product.id, ref.id);
      product.pdf = await this.uploadService.copyFile(product.pdf, newPdf);
    }
    for (const i of product.images) {
      const newPath = i.path.replace(product.id, ref.id);
      i.path = await this.uploadService.copyFile(i.path, newPath);
      const newThumb = i.thumb.replace(product.id, ref.id);
      i.thumb = await this.uploadService.copyFile(i.thumb, newThumb);
    }
    product.id = ref.id;
    product.technicalName = technicalName
    product.categories = [];
    return setDoc(ref, product).then(() => product.id);
  }

  /**
   * Delete the given product
   * @param product The product to delete
   */
  public async deleteProduct(product: CommerceProduct): Promise<void> {
    const organizationId = this.organizationService.currentOrganization.id;
    await this.uploadService.removeFolder(`organizations/${organizationId}/product/${product.id}`);
    await deleteDoc(doc(this.firestore, 'product', product.id));
  }

  /**
   * Update the product images
   * @param id The product id
   * @param images The list of product images
   * @returns void
   */
  updateProductImages(id: string, images: ImageModel[]): Promise<void> {
    const ref = doc(this.firestore, 'product', id).withConverter(CommerceProduct.converter);
    return updateDoc(ref, {
      images: images.filter(image => image.path).map(image => ({ ...image }))
    });
  }

  /**
   * Unsubscribe from products
   */
  unsubscribe(): void {
    if (this.unsubscribeProducts) {
      this.unsubscribeProducts();
      this.products$ = null;
    }
  }

  /**
   * Generates and returns a unique available ID for an organization based on the provided name.
   * If the generated ID is not available, appends a random suffix until a valid ID is found.
   * @param organizationId - The ID of the organization for which the unique ID is being generated.
   * @param name - The base name to use for generating the ID.
   * @returns A promise that resolves to the generated unique ID in lowercase.
   */
  async getAvailableId(organizationId: string, name: string): Promise<string> {
    let isValid = false;
    let oId = IdGeneratorHelper.slugify(name);
    do {
      isValid = await this.isAvailableTechnicalName(organizationId, oId);
      if (!isValid) {
        oId = oId + '-' + IdGeneratorHelper.makeId(5);
      }
    } while (!isValid);
    return oId.toLowerCase();
  }
}
